import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { ArrowLeft, Save } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import Skeleton from "components/Skeleton";
import PricingTemplatesEditor from "components/Processors/PricingTemplatesEditor";
import { isInternal } from "utilities/userType";
import {
  normalizePricingTemplates,
  validatePricingTemplates,
  pricingTemplatesPayload,
  pricingTemplateServerErrors,
  isPricingTemplateError,
} from "utilities/pricingTemplates";

const INDUSTRY_OPTIONS = [
  "Apparel",
  "Nutraceuticals",
  "Digital Goods",
  "Technology",
  "Health & Wellness",
  "Travel",
  "Gaming",
  "Subscription",
  "CBD/Hemp",
  "Adult",
  "Firearms",
  "Tobacco/Vape",
];

const ENTITY_TYPE_OPTIONS = [
  "LLC",
  "Corporation",
  "Sole Proprietorship",
  "Partnership",
  "Non-Profit",
];

const COUNTRY_OPTIONS = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "Netherlands",
  "Ireland",
  "Singapore",
  "Hong Kong",
];

const CARD_BRAND_OPTIONS = ["Visa", "Mastercard", "Amex", "Discover", "JCB", "UnionPay"];
const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY"];
const RISK_OPTIONS = ["low", "medium", "high"];
const BOARDING_OPTIONS = ["API", "portal", "manual"];
const INTEGRATION_TYPE_OPTIONS = ["gateway", "ISO", "direct"];
const SETTLEMENT_OPTIONS = ["gross", "net"];
const RESERVE_OPTIONS = ["none", "rolling", "upfront"];

const emptyForm = {
  name: "",
  supportedIndustries: [],
  supportedEntityTypes: [],
  supportedCountries: [],
  supportedCardBrands: [],
  supportedCurrencies: [],
  minMonthlyVolume: "",
  maxMonthlyVolume: "",
  minAverageTicket: "",
  maxAverageTicket: "",
  riskTolerance: "medium",
  maxChargebackRate: "",
  acceptsHighRisk: false,
  boardingMethod: "manual",
  integrationType: "gateway",
  settlementOptions: [],
  reserveOptions: [],
  notes: "",
  tags: "",
};

function ProcessorDetail() {
  const { processorId } = useParams();
  const isNew = processorId === "new" || !processorId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // SOUND-784: Kurv pricing templates. `null` = not editable here (a new
  // processor, or a non-internal caller) — and then the PUT does NOT carry the
  // field, because the API replaces the whole array and sending [] would wipe it.
  const [templates, setTemplates] = useState(null);
  const [templateErrors, setTemplateErrors] = useState({ rowErrors: {}, formError: null });

  // Fetch existing processor
  useEffect(() => {
    if (isNew) return;
    const fetchProcessor = async () => {
      setLoading(true);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getProcessor(processorId),
          { id: user.id, accessToken: user.accessToken },
          user.companyId
        );
        const p = res.data?.data;
        if (p) {
          setForm({
            name: p.name || "",
            supportedIndustries: p.supportedIndustries || [],
            supportedEntityTypes: p.supportedEntityTypes || [],
            supportedCountries: p.supportedCountries || [],
            supportedCardBrands: p.supportedCardBrands || [],
            supportedCurrencies: p.supportedCurrencies || [],
            minMonthlyVolume: p.minMonthlyVolume ?? "",
            maxMonthlyVolume: p.maxMonthlyVolume ?? "",
            minAverageTicket: p.minAverageTicket ?? "",
            maxAverageTicket: p.maxAverageTicket ?? "",
            riskTolerance: p.riskTolerance || "medium",
            maxChargebackRate: p.maxChargebackRate ?? "",
            acceptsHighRisk: p.acceptsHighRisk ?? false,
            boardingMethod: p.boardingMethod || "manual",
            integrationType: p.integrationType || "gateway",
            settlementOptions: p.settlementOptions || [],
            reserveOptions: p.reserveOptions || [],
            notes: p.notes || "",
            tags: (p.tags || []).join(", "),
          });
          // Absent (stripped for non-internal, or an API without SOUND-784)
          // reads as none configured.
          // (The effect only runs for an existing processor.)
          setTemplates(isInternal(user) ? normalizePricingTemplates(p.pricingTemplates) : null);
        }
      } catch (err) {
        console.error("[ProcessorDetail] Failed to fetch", err);
        toast({
          status: "destructive",
          title: "Failed to load processor",
          description: err.response?.data?.error || "Unknown error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProcessor();
  }, [processorId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayValue = (field, value) => {
    setForm((prev) => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value],
      };
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast({ status: "warning", title: "Processor name is required" });
      return;
    }

    // SOUND-784: same rules as the API, checked before the round trip.
    if (templates) {
      const check = validatePricingTemplates(templates);
      setTemplateErrors({ rowErrors: check.rowErrors, formError: check.formError });
      if (!check.valid) {
        toast({ status: "warning", title: "Fix the pricing templates before saving" });
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        supportedIndustries: form.supportedIndustries,
        supportedEntityTypes: form.supportedEntityTypes,
        supportedCountries: form.supportedCountries,
        supportedCardBrands: form.supportedCardBrands,
        supportedCurrencies: form.supportedCurrencies,
        minMonthlyVolume: form.minMonthlyVolume !== "" ? Number(form.minMonthlyVolume) : null,
        maxMonthlyVolume: form.maxMonthlyVolume !== "" ? Number(form.maxMonthlyVolume) : null,
        minAverageTicket: form.minAverageTicket !== "" ? Number(form.minAverageTicket) : null,
        maxAverageTicket: form.maxAverageTicket !== "" ? Number(form.maxAverageTicket) : null,
        riskTolerance: form.riskTolerance,
        maxChargebackRate: form.maxChargebackRate !== "" ? Number(form.maxChargebackRate) : null,
        acceptsHighRisk: form.acceptsHighRisk,
        boardingMethod: form.boardingMethod,
        integrationType: form.integrationType,
        settlementOptions: form.settlementOptions,
        reserveOptions: form.reserveOptions,
        notes: form.notes,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        // Whole-array replace — every row, every time, and only when loaded.
        ...(templates ? { pricingTemplates: pricingTemplatesPayload(templates) } : {}),
      };

      if (isNew) {
        const res = await makeApiRequest(
          "post",
          apiRoutes.createProcessor,
          { id: user.id, accessToken: user.accessToken },
          user.companyId,
          payload
        );
        const created = res.data?.data;
        toast({ status: "success", title: `Processor "${created?.name}" created` });
        navigate(`/processors/${created?.processorId}`, { replace: true });
      } else {
        await makeApiRequest(
          "put",
          apiRoutes.updateProcessor(processorId),
          { id: user.id, accessToken: user.accessToken },
          user.companyId,
          payload
        );
        toast({ status: "success", title: "Processor updated" });
        // Saved rows can no longer be removed, only deactivated.
        if (templates) setTemplates(normalizePricingTemplates(payload.pricingTemplates));
      }
    } catch (err) {
      console.error("[ProcessorDetail] Save failed", err);
      const body = err.response?.data;
      // A refusal about the templates is pinned inline on its rows.
      if (templates && err.response?.status === 400 && isPricingTemplateError(body)) {
        setTemplateErrors(pricingTemplateServerErrors(body));
      }
      toast({
        status: "destructive",
        title: isNew ? "Failed to create processor" : "Failed to update processor",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton.Rect className="h-8 w-48 mb-4" />
        <Skeleton.Rect className="h-5 w-full mb-2" />
        <Skeleton.Rect className="h-5 w-2/3" />
      </div>
    );
  }

  const CheckboxGroup = ({ label, field, options }) => (
    <div>
      <label className="text-sm font-medium text-foreground mb-2 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <label
            key={opt}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer border transition-colors ${
              form[field].includes(opt)
                ? "border-brand bg-brand/10 text-brand"
                : "border-border text-muted-foreground hover:border-brand/50"
            }`}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={form[field].includes(opt)}
              onChange={() => toggleArrayValue(field, opt)}
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={isNew ? "Add Processor" : form.name || "Processor"}
        subtitle={isNew ? "Register a new payment processor." : "Edit processor details."}
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : isNew ? "Create" : "Save"}
            </button>
          </div>
        }
      />

      <div className="container py-6 space-y-6">
        {/* Basic Info */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Basic Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="e.g. Elavon, First Data"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Tags</label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={form.tags}
                onChange={(e) => updateField("tags", e.target.value)}
                placeholder="high-risk, eu (comma-separated)"
              />
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-5">
          <h2 className="text-lg font-semibold">Capabilities</h2>
          <CheckboxGroup
            label="Supported Industries"
            field="supportedIndustries"
            options={INDUSTRY_OPTIONS}
          />
          <CheckboxGroup
            label="Supported Entity Types"
            field="supportedEntityTypes"
            options={ENTITY_TYPE_OPTIONS}
          />
          <CheckboxGroup
            label="Supported Countries"
            field="supportedCountries"
            options={COUNTRY_OPTIONS}
          />
          <CheckboxGroup
            label="Supported Card Brands"
            field="supportedCardBrands"
            options={CARD_BRAND_OPTIONS}
          />
          <CheckboxGroup
            label="Supported Currencies"
            field="supportedCurrencies"
            options={CURRENCY_OPTIONS}
          />
        </section>

        {/* Volume Constraints */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Volume Constraints</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Min Monthly Volume ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.minMonthlyVolume}
                onChange={(e) => updateField("minMonthlyVolume", e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Max Monthly Volume ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.maxMonthlyVolume}
                onChange={(e) => updateField("maxMonthlyVolume", e.target.value)}
                placeholder="Leave blank for unlimited"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Min Average Ticket ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.minAverageTicket}
                onChange={(e) => updateField("minAverageTicket", e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Max Average Ticket ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.maxAverageTicket}
                onChange={(e) => updateField("maxAverageTicket", e.target.value)}
                placeholder="Leave blank for unlimited"
              />
            </div>
          </div>
        </section>

        {/* Risk Profile */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Risk Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Risk Tolerance</label>
              <select
                className="select select-bordered w-full"
                value={form.riskTolerance}
                onChange={(e) => updateField("riskTolerance", e.target.value)}
              >
                {RISK_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Max Chargeback Rate (%)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.maxChargebackRate}
                onChange={(e) => updateField("maxChargebackRate", e.target.value)}
                placeholder="e.g. 2.0"
                step="0.1"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                className="checkbox"
                checked={form.acceptsHighRisk}
                onChange={(e) => updateField("acceptsHighRisk", e.target.checked)}
                id="acceptsHighRisk"
              />
              <label htmlFor="acceptsHighRisk" className="text-sm font-medium text-foreground cursor-pointer">
                Accepts High Risk
              </label>
            </div>
          </div>
        </section>

        {/* Operational */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-5">
          <h2 className="text-lg font-semibold">Operational</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Boarding Method</label>
              <select
                className="select select-bordered w-full"
                value={form.boardingMethod}
                onChange={(e) => updateField("boardingMethod", e.target.value)}
              >
                {BOARDING_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Integration Type</label>
              <select
                className="select select-bordered w-full"
                value={form.integrationType}
                onChange={(e) => updateField("integrationType", e.target.value)}
              >
                {INTEGRATION_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          <CheckboxGroup
            label="Settlement Options"
            field="settlementOptions"
            options={SETTLEMENT_OPTIONS}
          />
          <CheckboxGroup
            label="Reserve Options"
            field="reserveOptions"
            options={RESERVE_OPTIONS}
          />
        </section>

        {/* Pricing templates — SOUND-784, internal-only */}
        {templates && (
          <PricingTemplatesEditor
            templates={templates}
            onChange={(next) => {
              setTemplates(next);
              setTemplateErrors({ rowErrors: {}, formError: null });
            }}
            rowErrors={templateErrors.rowErrors}
            formError={templateErrors.formError}
            disabled={saving}
          />
        )}

        {/* Notes */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Notes</h2>
          <textarea
            className="input input-bordered w-full"
            rows={4}
            value={form.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder="Internal notes about this processor..."
          />
        </section>
      </div>
    </div>
  );
}

export default requireAuth(ProcessorDetail);
